<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
      </ion-toolbar>
      <ion-toolbar class="breadcrumb-bar" v-if="activeSection">
        <div class="breadcrumb">
          <span class="crumb-link" @click="activeSection = null">{{ t("Tactics Gym") }}</span>
          <span class="crumb-sep">›</span>
          <span class="crumb-current">{{ activeSection.label }}</span>
        </div>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true">
      <Transition name="slide-up" mode="out-in">

        <!-- Top-level tile grid -->
        <div v-if="!activeSection" class="tile-grid" key="tiles">
          <div
            v-for="section in sections"
            :key="section.id"
            class="tile tile-teal"
            :class="{ 'tile-full': section.fullWidth }"
            @click="openSection(section)"
          >
            <ion-icon :icon="section.icon" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ section.label }}</span>
          </div>
        </div>

        <!-- Drill-down list -->
        <div v-else class="drill-down" key="list">
          <ion-list :inset="true">
            <ion-item
              v-for="item in activeSection.items"
              :key="item.id"
              button
              :detail="true"
              @click="navigateTo(item)"
            >
              <div class="item-icon-wrap" slot="start">
                <ion-icon :icon="item.icon" class="item-icon" aria-hidden="true" />
              </div>
              <ion-label>
                <h2>{{ item.label }}</h2>
                <p v-if="item.description">{{ item.description }}</p>
              </ion-label>
            </ion-item>
          </ion-list>
          <div v-if="activeSection.id == 'puzzles' || activeSection.id == 'visualization'" class="list-footer">
            {{ puzzleFreeMessage }}
          </div>
          <div v-if="activeSection.id == 'blindfold' " class="list-footer">
            {{ blindfoldMessage }}
          </div>
        </div>

      </Transition>
    </ion-content>

    <BottomNav @bottom-click="onClick"/>
  </ion-page>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import {
    IonPage, IonHeader, IonToolbar, IonTitle,
    IonButtons, IonBackButton, IonContent,
    IonIcon, IonList, IonItem, IonLabel,
  } from '@ionic/vue'
  import {
    extensionPuzzleOutline, eyeOutline, eyeOffOutline, timeOutline,
    downloadOutline, listOutline, barbellOutline,
    shieldCheckmarkOutline, gitBranchOutline, swapHorizontal, playCircleOutline
  } from 'ionicons/icons'
  import BottomNav from '@/components/BottomNav.vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { t } from '@/composables/translateText.js'

  const emit = defineEmits(['menu-item-click'])

  const connectionStore = useConnectionStore()

  var attemptCountString = connectionStore.getPuzzleAttemptCount
  var puzzleCountString = connectionStore.getPuzzleCount
  var showOfflineToggle = false
  var offlineMenuItemLabel = ""

  const puzzleFreeMessage = t("All exercises here are free — no subscription needed.")
  const subscriberMessage = t("Thanks for being a Subscriber! -- You have access to the Komodo, Stockfish and Maia Engines at all skill levels. Plus All Blindfold Stepping Stones.")
  const freeMessage = t("Your free account gives access to the Komodo Engine at its easiest setting only. For blindfold chess, you'll have two 'stepping stone' levels. See the 'Subscribe' screen for all the subscriber benefits")
  const blindfoldMessage = (connectionStore.isSubscriber == 1) ? subscriberMessage : freeMessage
  if (parseInt(attemptCountString) > 0) {
    offlineMenuItemLabel = t("Sync Offline Attempts & Refresh")
    showOfflineToggle = true
  }
  else
  if (parseInt(puzzleCountString) > 0) {
    offlineMenuItemLabel = t("Puzzles Available when Offline")
    showOfflineToggle = true
  }
  else {
    offlineMenuItemLabel = t("Download for Offline Play")
  }

  var workOfflinePuzzles = "no"
  if (showOfflineToggle)
    workOfflinePuzzles = connectionStore.getWorkOfflinePuzzles 
  const workOfflineTextAdder = (workOfflinePuzzles == "yes") ? " (" + t("Offline") + ")" : ""

  const toggleOfflineLabel = (workOfflinePuzzles == "yes") ? t("Switch to Online Puzzles") : t("Switch to Offline Puzzles")

  const openGameId = connectionStore.getPlayComputerGameId

  interface MenuItem {
    id: string
    label: string
    description?: string
    icon: string
    route: string
  }

  interface Section {
    id: string
    label: string
    icon: string
    fullWidth?: boolean
    items: MenuItem[]
  }

  const blindfoldMenu : MenuItem[] = [
        { id: 'play-continue',    label: t('Continue Game?'),    description: t('Pick up your game in progress'),  
                                  icon: playCircleOutline, route: 'play_continue' },
        { id: 'play-computer',    label: t('Play Computer'),    description: t('Play a full game against the engine'),  
                                  icon: barbellOutline, route: 'play_computer' },
        { id: 'play-blindfold',   label: t('Play Blindfold'),   description: t('Play with no board visible'),           
                                  icon: eyeOffOutline,  route: 'play_blindfold' },
        { id: 'game-history',     label: t('Game History'),     description: t('Review your past blindfold games'),     
                                  icon: listOutline,    route: 'blindfold_history' },
        { id: 'blindfold-puzzle', label: t('Blindfold Puzzles'),description: t('Solve puzzles without a visible board'),
                                  icon: extensionPuzzleOutline,  route: 'puzzle_blindfold' },
      ]
  
  const sections: Section[] = [
    {
      id: 'puzzles',
      label: t('Puzzles'),
      icon: extensionPuzzleOutline,
      fullWidth: true,
      items: [
        { id: 'puzzles',          label: t('Puzzles') + workOfflineTextAdder,                    description: t('Solve tactical puzzles'),                    
                                  icon: extensionPuzzleOutline,route: 'puzzles' },
        { id: 'puzzle-viz',       label: t('Puzzle Visualization') + workOfflineTextAdder,       description: t('Solve the Puzzle. Visualized In Your Mind'), 
                                  icon: eyeOutline,            route: 'puzzle_visualize' },
        { id: 'timed',            label: t('Timed Puzzles') + workOfflineTextAdder,              description: t('Race the clock on tactical challenges'),     
                                  icon: timeOutline,           route: 'puzzle_timed' },
        { id: 'timed-viz',        label: t('Timed Visualized Puzzles') + workOfflineTextAdder,   description: t('Visualization puzzles with a time limit'),   
                                  icon: timeOutline,           route: 'puzzle_timed_visualize' },
        { id: 'offline',          label: offlineMenuItemLabel,         description: t('Save puzzles to play without a connection'), 
                                  icon: downloadOutline,       route: 'puzzle_offline' },
        { id: 'switch-offline',   label: toggleOfflineLabel,           description: t("Play from your downloaded collection"),              
                                  icon: swapHorizontal,        route: 'puzzle_offline_toggle' },
        { id: 'history',          label: t('Puzzle History & Download'),  description: t('Review and export your past puzzles'),       
                                  icon: listOutline,           route: 'puzzle_history' },
      ],
    },
    {
      id: 'visualization',
      label: t('Visualization'),
      icon: eyeOutline,
      items: [
        { id: 'guarding',    label: t("Who's Guarding Whom"),       description: t('Identify piece relationships'),            
                             icon: shieldCheckmarkOutline, route: 'guarding' },
        { id: 'static',      label: t('Static Boards'),             description: t('Study positions without moving pieces'),   
                             icon: eyeOutline,             route: 'static' },
        { id: 'random',      label: t('Moves at Random Location'),  description: t('Visualize moves from any board position'), 
                             icon: gitBranchOutline,       route: 'moves_random' },
        { id: 'game-start',  label: t('Moves from Game Beginning'), description: t('Trace moves from the opening'),           
                             icon: gitBranchOutline,       route: 'moves_begin' },
        { id: 'pieces',      label: t('Follow Specific Pieces'),    description: t('Track individual pieces through a game'),  
                             icon: eyeOutline,             route: 'moves_pieces' },
      ],
    },
    {
      id: 'blindfold',
      label: t('Blindfold'),
      icon: eyeOffOutline,
      items: (openGameId != "0") ? blindfoldMenu : blindfoldMenu.slice(1),
    },
  ]

  const activeSection = ref<Section | null>(null)

  // is there a secion id coming back

  const sectionInUse = connectionStore.getMenuSectionInUse
  if (sectionInUse != null && sectionInUse != "") {
    for (var i=0; i < sections.length; i++)
      if (sectionInUse == sections[i].id) {
        activeSection.value = sections[i]
        break
      }
  }

  function openSection(section: Section) {
    activeSection.value = section
  }

  function navigateTo(item: MenuItem) {
    console.log("tactics item",item)

    // set the return values for clicking on the done button
    connectionStore.setMenuInUse("tactics_gym")
    connectionStore.setMenuSectionInUse(activeSection.value.id)

    if (item.route == "puzzle_offline_toggle") {
      var workOfflinePuzzles = connectionStore.getWorkOfflinePuzzles
      workOfflinePuzzles = (workOfflinePuzzles == "no") ? "yes" : "no"
      connectionStore.setWorkOfflinePuzzles(workOfflinePuzzles)
    }
    onClick(item.route,item.label,"FROM_NEW_MENU")
  }

  function onClick(path : string,label : string,whichMenu : string) {
    console.log("Landing page CLICK ",path,label)
    connectionStore.setIsUsingNewMenus(true)
    emit("menu-item-click",path,label,whichMenu)
  }

</script>

<style scoped>

.list-footer {
  font-size: 12px;
  color: #5DCAA5;
  text-align: left;
  padding: 0px 68px 24px;
}

ion-toolbar {
  --background: #12122a;
  --color: #ffffff;
  --border-color: #2a2a4a;
}

ion-back-button {
  --color: #9FE1CB;
}

ion-title {
  font-size: 17px;
  font-weight: 500;
}

.breadcrumb-bar {
  --background: #0f0f22;
  --min-height: 32px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  font-size: 16px;
}

.crumb-link {
  color: #9FE1CB;
  cursor: pointer;
}

.crumb-sep  { color: #444466; }
.crumb-current { color: #888880; }

ion-content { --background: #1a1a2e; }

/* ── Tile grid ── */
.tile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
}

.tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 18px;
  padding: 28px 16px 22px;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
}

.tile:active {
  opacity: 1.0;
  transform: scale(0.97);
}

.tile-full {
  grid-column: 1 / -1;
  flex-direction: row;
  padding: 22px 16px;
  gap: 14px;
  flex-direction: column;
}

.tile-teal { background: #085041; }
.tile-teal .tile-icon,
.tile-teal .tile-label { color: #9FE1CB; }

.tile-icon  { font-size: 34px; }
.tile-full .tile-icon { font-size: 26px; }

.tile-label {
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  line-height: 1.3;
}

/* ── Drill-down list ── */
.drill-down { padding-top: 8px; }

ion-list { --background: transparent; background: transparent; }

ion-item {
  --background: #1e1e35;
  --background-activated: #2a2a45;
  --color: #e0e0f0;
  --border-color: #2a2a4a;
  --detail-icon-color: #1D9E75;
  --detail-icon-opacity: 1;
  --padding-start: 12px;
  --inner-padding-end: 12px;
  --min-height: 62px;
}

ion-item h2 {
  font-size: 15px;
  font-weight: 400;
  color: #e0e0f0;
  margin-bottom: 3px;
}

ion-item p {
  font-size: 12px;
  color: #888880;
  line-height: 1.4;
  white-space: normal;
}

.item-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #085041;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;
}

.item-icon {
  font-size: 19px;
  color: #9FE1CB;
}

/* ── Transition ── */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.slide-up-enter-from { opacity: 0; transform: translateY(12px); }
.slide-up-leave-to   { opacity: 0; transform: translateY(-8px); }
</style>
